import { ReactNode } from "react";
import { DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { ContractStandardField, EntityTemplateField, getStandardFieldTranslationKey } from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel } from "../contract/Contract.types.ts";
import { CustomFieldRenderer, StandardFieldRenderer } from "../ContractFieldRenderers.tsx";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { ValidationError } from "@common/validationError.ts";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect.tsx";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { ContractStatus } from "../Contracts.types.ts";
import { useContractStatusOptions } from "../contractOverview/contractStatusSelector/ContractStatusSelector.hooks.ts";
import { useTranslation } from "@localization/useTranslation.ts";

export interface ContractTemplateFieldProps {
    field: EntityTemplateField;
    contract: Partial<ContractModel>;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tagIds: Array<string>) => void;
    readOnly?: boolean;
    disabled?: boolean;
    /** The review shows every field twice, so its help icons are noise rather than help. */
    hideToolTip?: boolean;
    /** The review's wrapper owns the spacing between rows, so the fields must not add their own. */
    noMargin?: boolean;
    /** The review gives the renewal period a row and an arrow of its own, so the end date must not draw it. */
    hideAutoRenewal?: boolean;
    /**
     * The party editor to show where the template places Counterparties. Left out, the field renders
     * nothing: the standard renderer saves each edit straight to the server, which neither a contract
     * being created nor a review waiting for Save can allow.
     */
    counterPartiesSlot?: ReactNode;
    /** Puts the cursor in the name field, for a form whose first thing to fill in is the name. */
    autoFocusName?: boolean;
    /** Reports rich text on every edit instead of on blur, for a form whose Save button depends on the value. */
    reportEveryChange?: boolean;
    /** Puts each label above its input with the help icon beside it, for a form laid out that way. */
    useFixedLabel?: boolean;
}

/**
 * One template field, rendered for a form that has not saved yet. Shared by the AI create panel and the
 * AI-assisted update review so the two show the customer's fields identically.
 *
 * Every branch here replaces a standard renderer that either writes to the server on its own or needs a
 * real entity id to address. The rest go through the standard renderers untouched.
 */
function ContractTemplateField({
    field,
    contract,
    onChange,
    validationErrors,
    selectedTagIds,
    onTagsChange,
    readOnly = false,
    disabled: disabledByCaller = false,
    hideToolTip = false,
    noMargin = false,
    hideAutoRenewal = false,
    counterPartiesSlot,
    autoFocusName = false,
    reportEveryChange = false,
    useFixedLabel = false,
}: ContractTemplateFieldProps) {
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const { options: statusOptions } = useContractStatusOptions();
    const disabled = disabledByCaller || field.disabled;

    // Passing the real contract id would make useCustomField PUT and TagsMultiSelectAutoUpdater save on
    // every keystroke, before the user has pressed Save. An empty id is what keeps both quiet.
    const contractId = "";

    if (field.standardFieldIdentifier === ContractStandardField.Tags) {
        return (
            <TagsMultiSelect
                selectedTags={selectedTagIds}
                onChange={readOnly ? undefined : onTagsChange}
                entityType={TagRelationType.Contract}
                isLoading={false}
                disabled={disabled}
                noMargin={noMargin}
                useFixedLabel={useFixedLabel}
                errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Tags))?.error}
            />
        );
    }

    // ContractStatusField opens the archive dialog against a contract id, so an unsaved form gets the plain
    // select instead. Archiving also ends the contract, which only that dialog does, so it is not offered here
    if (field.standardFieldIdentifier === ContractStandardField.Status) {
        return (
            <DotLegalSelect
                label={translateString("status")}
                useFixedLabel={useFixedLabel}
                selectedItem={contract.status === undefined ? null : Number(contract.status).toString()}
                noOptionsLabel={""}
                onChange={(event) =>
                    onChange((c) => {
                        c.status = parseInt(event!) as ContractStatus;
                    })
                }
                options={statusOptions.filter((option) => option.id !== ContractStatus.Archived.toString())}
                noMargin={noMargin}
                disabled={disabled || readOnly}
                disableClearable
            />
        );
    }

    if (field.standardFieldIdentifier === ContractStandardField.CounterParties) {
        return <>{counterPartiesSlot}</>;
    }

    // The documents table manages a saved contract's files directly and has nothing to propose or revert.
    if (field.standardFieldIdentifier === ContractStandardField.Documents) {
        return null;
    }

    if (field.standardFieldIdentifier) {
        return (
            <StandardFieldRenderer
                field={field}
                contract={contract as ContractModel}
                contractId={contractId}
                onChange={onChange}
                onRefetchContract={() => {}}
                validationErrors={validationErrors}
                disabled={disabled}
                readOnly={readOnly}
                isPreview={false}
                isLoading={false}
                hideToolTip={hideToolTip}
                noMargin={noMargin}
                hideAutoRenewal={hideAutoRenewal}
                autoFocusName={autoFocusName}
                reportEveryChange={reportEveryChange}
                useFixedLabel={useFixedLabel}
                permissions={permissions}
                disableAccessWarning
                showWarningAsInfo
            />
        );
    }

    return (
        <CustomFieldRenderer
            field={field}
            contract={contract as ContractModel}
            entityId={contractId}
            onChange={onChange}
            validationErrors={validationErrors}
            disabled={disabled}
            readOnly={readOnly}
            hideToolTip={hideToolTip}
            noMargin={noMargin}
            useFixedLabel={useFixedLabel}
            isPreview={false}
            isLoading={false}
        />
    );
}

export default ContractTemplateField;
